import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const InquiryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`inquiries/${id}`)
      .then((res) => setInquiry(res.data))
      .catch((err) => {
        console.error("Error fetching inquiry details:", err);
        alert("Failed to load inquiry details");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleViewPDF = async () => {
    if (!inquiry) return;
    const doc = new jsPDF();
    const themeColor = [0, 86, 179]; // Rekonic blue

    // --- Logo ---
    try {
      const logoUrl = process.env.PUBLIC_URL + "/rekonic-logo.jpg";
      const logoImg = await new Promise((resolve) => {
        const img = new Image();
        img.src = logoUrl;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
      if (logoImg) {
        doc.addImage(logoImg, "JPEG", 15, 10, 50, 20);
      }
    } catch (e) {
      console.error("Logo failed to load", e);
    }

    // --- Title ---
    doc.setFontSize(22);
    doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.text("INQUIRY REPORT", 195, 25, { align: "right" });

    // --- Divider ---
    doc.setDrawColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.setLineWidth(0.8);
    doc.line(15, 35, 195, 35);

    // --- Inquiry Meta Info Table ---
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const rows = [
      ["Inquiry ID", `#${inquiry.id}`],
      ["Subject", inquiry.subject || "N/A"],
      ["Type", inquiry.inquiry_type || "N/A"],
      ["Priority", inquiry.priority || "N/A"],
      ["Status", inquiry.status || "N/A"],
      ["Source", inquiry.source || "N/A"],
      ["Branch", inquiry.branch_name || inquiry.branch_id || "N/A"],
      ["Staff", inquiry.staff_name || inquiry.staff_id || "N/A"],
      ["Customer", inquiry.customer_name || inquiry.customer_id || "N/A"],
      ["Created At", new Date(inquiry.created_at).toLocaleString()],
      [
        "Due Date",
        inquiry.due_date
          ? new Date(inquiry.due_date).toLocaleString()
          : "N/A",
      ],
    ];

    autoTable(doc, {
      startY: 42,
      head: [["Field", "Value"]],
      body: rows,
      headStyles: {
        fillColor: themeColor,
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 50 } },
      margin: { left: 15, right: 15 },
    });

    const afterTable = doc.lastAutoTable.finalY + 10;

    // --- Description ---
    if (inquiry.description) {
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
      doc.text("Description:", 15, afterTable);
      doc.setFont(undefined, "normal");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      const descLines = doc.splitTextToSize(inquiry.description, 175);
      doc.text(descLines, 15, afterTable + 7);
    }

    // --- Resolution Notes ---
    if (inquiry.resolution_notes) {
      const notesY =
        afterTable +
        (inquiry.description
          ? doc.splitTextToSize(inquiry.description, 175).length * 5 + 15
          : 10);
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
      doc.text("Resolution Notes:", 15, notesY);
      doc.setFont(undefined, "normal");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      const noteLines = doc.splitTextToSize(inquiry.resolution_notes, 175);
      doc.text(noteLines, 15, notesY + 7);
    }

    // --- Footer ---
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on ${new Date().toLocaleString()} — Rekonic`,
      105,
      pageHeight - 10,
      { align: "center" }
    );

    doc.save(`Inquiry_${inquiry.id}.pdf`);
  };

  if (loading) return <div className="container mt-4"><p>Loading inquiry details...</p></div>;
  if (!inquiry) return <div className="container mt-4"><p>Inquiry not found.</p></div>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Inquiry Details #{inquiry.id}</h4>
        <div className="d-flex gap-2">
          <button
            className="btn btn-danger"
            onClick={handleViewPDF}
            title="Download as PDF"
          >
            📄 View as PDF
          </button>
          <button className="btn btn-primary" onClick={() => navigate(`/dashboard/inquiries/edit/${inquiry.id}`)}>
            Edit Inquiry
          </button>
          <button className="btn btn-secondary" onClick={() => navigate("/dashboard/inquiries/list")}>
            Back to List
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Subject:</strong> {inquiry.subject}
            </div>
            <div className="col-md-6">
              <strong>Type:</strong> {inquiry.inquiry_type}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Branch:</strong> {inquiry.branch_name || inquiry.branch_id}
            </div>
            <div className="col-md-6">
              <strong>Staff:</strong> {inquiry.staff_name || inquiry.staff_id}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Customer:</strong> {inquiry.customer_name || inquiry.customer_id || "N/A"}
            </div>
            <div className="col-md-6">
              <strong>Priority:</strong> {inquiry.priority}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Source:</strong> {inquiry.source || "N/A"}
            </div>
            <div className="col-md-6">
              <strong>Status:</strong> {inquiry.status}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Created At:</strong> {new Date(inquiry.created_at).toLocaleString()}
            </div>
            <div className="col-md-6">
              <strong>Due Date:</strong> {inquiry.due_date ? new Date(inquiry.due_date).toLocaleString() : "N/A"}
            </div>
          </div>

          <div className="mb-3">
            <strong>Description:</strong>
            <div className="p-3 bg-light border rounded mt-2">
              {inquiry.description || "No description provided."}
            </div>
          </div>

          {inquiry.resolution_notes && (
            <div className="mb-3">
              <strong>Resolution Notes:</strong>
              <div className="p-3 bg-light border rounded mt-2">
                {inquiry.resolution_notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InquiryDetail;
